import { ComponentProps, PropsWithChildren, ReactNode } from 'react';
import { Button } from '@warp-ds/react';
import LoadingScreen from './LoadingScreen.js';
import ExitWithConfirmation from './ExitConfirmation.js';
import TopNavigator from './TopNavigator.js';
import IconClose24 from '@warp-ds/icons/react/close-24';
import ProgressBar from '../ProgressBar/index.js';
import WrappedLoadingSkeleton from '../LoadingSkeleton/WrappedLoadingSkeleton/index.js';

/**
 * A layout component for a page in a process flow
 *
 * This component is a wrapper for Process Flows and renders a header and footer around the content for navigating and exiting the flow. The layout supports both mobile and desktop views.
 *
 * Optional graphical elements are:
 *
 * - A save button and a back button.
 * - A progress bar to show how far in the process flow the user is.
 * - A confirmation modal showing before exiting the flow.
 * - A title and a description for the page.
 *
 * The wrapper also support loading states.
 *
 */
type Props = {
    /** If true, the layout will be rendered as mobile. */
    isMobile: boolean;
    /** Props for exit button. Button will be rendered into a warp button. Children and onClick are required.  */
    exitButton: Required<Pick<ComponentProps<typeof Button>, 'children' | 'onClick'>>;
    /** Texts for a confirmation modal before exiting. If undefined the flow will be exited staight away. */
    confirmExitModal?: { title: string; confirm: string; abort: string };
    /** Show a progress bar. Provide the total number of steps and the current step in the process flow, to show a progressBar .  */
    progessBar?: { step: number; numberOfSteps: number };
    /** An optional html element that will show above the navigation buttons */
    summaryFooterSection?: ReactNode;
    /** Props for the button for saving the current step. Button will be rendered into a warp button and type is equal to warp ButtonProps. Children is required.  */
    saveButton?: ComponentProps<typeof Button>;
    /** Props for back button. Button will be rendered into a warp button and type is equal to warp ButtonProps. Children and onClick is required.   */
    backButton?: Required<Pick<ComponentProps<typeof Button>, 'children' | 'onClick'>> &
        Omit<ComponentProps<typeof Button>, 'children' | 'onClick'>;
    /** Title for the page */
    title?: string;
    /** Description for the page */
    description?: string;
    /** If true, a loading spinner or skeleton will be shown instead of the children */
    isLoading?: boolean;
};

const desktopButtonCss = 'h-56 w[204] min-w-[204]';

const ProcessFlowWrapper = ({
    progessBar,
    summaryFooterSection,
    children,
    saveButton,
    backButton,
    exitButton,
    confirmExitModal,
    isMobile,
    title,
    description,
    isLoading,
}: PropsWithChildren<Props>) => {
    const showStickyFooter = saveButton;
    if (isMobile) {
        return (
            <div className="h-full flex flex-col w-full mx-auto ">
                <div
                    className={`h-full page-container flex flex-col grow-1 relative ${showStickyFooter && 'mb-96'}`}
                >
                    {isLoading ? (
                        <LoadingScreen />
                    ) : (
                        <>
                            <TopNavigator
                                prevButton={backButton}
                                exitButton={exitButton}
                                confirmExitModal={confirmExitModal}
                            />
                            <h1>{title}</h1>
                            {description && <p className="mb-32">{description}</p>}
                            {children}
                        </>
                    )}
                </div>

                {showStickyFooter && (
                    <div
                        className={`sticky bottom-0 s-bg ${progessBar ? '' : 'border-t-1 s-border-primary-subtle'}`}
                    >
                        {progessBar && (
                            <ProgressBar
                                step={progessBar.step}
                                numberOfSteps={progessBar.numberOfSteps}
                            />
                        )}
                        <div className="py-16 flex flex-col gap-12 page-container">
                            {summaryFooterSection && (
                                <div className="-mt-4">{summaryFooterSection}</div>
                            )}
                            {saveButton && (
                                <Button
                                    primary
                                    type="submit"
                                    className="w-full min-w-full h-56"
                                    {...saveButton}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div>
            {progessBar && (
                <div className="sticky s-bg">
                    <ProgressBar
                        size="large"
                        step={progessBar.step}
                        numberOfSteps={progessBar.numberOfSteps}
                    />
                </div>
            )}
            <div className="page-container">
                <div className="h-full flex flex-col mt-32 lg:w-3/4 mx-auto tjt-max-w-500">
                    <>
                        <div className="flex justify-between">
                            <h1>
                                <WrappedLoadingSkeleton isLoading={Boolean(isLoading)}>
                                    <>{title}</>
                                </WrappedLoadingSkeleton>
                            </h1>
                            <ExitWithConfirmation
                                onExitFlow={{
                                    ...exitButton,
                                    children: (
                                        <>
                                            <IconClose24 className="s-icon-subtle-active" />
                                            <span className="sr-only">{exitButton.children}</span>
                                        </>
                                    ),
                                }}
                                confirmModal={confirmExitModal}
                            />
                        </div>
                        {description && (
                            <p className="mb-32">
                                <WrappedLoadingSkeleton isLoading={Boolean(isLoading)}>
                                    <>{description}</>
                                </WrappedLoadingSkeleton>
                            </p>
                        )}
                        <WrappedLoadingSkeleton isLoading={Boolean(isLoading)}>
                            {children}
                        </WrappedLoadingSkeleton>
                    </>

                    {summaryFooterSection && (
                        <div className="mt-32">
                            <WrappedLoadingSkeleton isLoading={Boolean(isLoading)}>
                                {summaryFooterSection}
                            </WrappedLoadingSkeleton>
                        </div>
                    )}

                    <div className="flex justify-end my-24">
                        <div className="flex gap-8">
                            {backButton && (
                                <Button
                                    secondary
                                    quiet
                                    className={desktopButtonCss}
                                    {...backButton}
                                />
                            )}
                            {saveButton && (
                                <Button
                                    primary
                                    type="submit"
                                    className={desktopButtonCss}
                                    {...saveButton}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProcessFlowWrapper;
